import React, { useState, useEffect, useCallback } from 'react';
import { serviceRatesService } from '../services/serviceRates.service';
import { vendorRegistrationService } from '../services/vendorRegistration.service';
import { useAuth } from '../contexts/AuthContext';
import { Plus, ChevronDown, ChevronRight, Edit } from 'lucide-react';
import AddServiceRateRow from '../components/AddServiceRateRow';
import EditServiceRateRow from '../components/EditServiceRateRow';
import localeConfig from '../config/locale.config';
import { formatDate } from '../utils/helpers';

const ServiceRates = () => {
    const { user } = useAuth();
    const [serviceRates, setServiceRates] = useState([]);
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterActive, setFilterActive] = useState(null);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [showAddServiceRateRow, setShowAddServiceRateRow] = useState(false);
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
    const [serviceRateToDeactivate, setServiceRateToDeactivate] = useState(null);
    const [editingServiceRateId, setEditingServiceRateId] = useState(null);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [serviceSubtypes] = useState({});
    const [newServiceRate, setNewServiceRate] = useState({
        service_type_code: '',
        service_subtype_code: '',
        time_slot: 'DAY',
        time_slot_start: '06:00:00',
        time_slot_end: '18:00:00',
        base_service_rate: '',
        base_max_kms: '',
        additional_rate_per_km: '',
        fixed_rate: '',
        cancel_charges_per_km_upto_base_kms: '',
        cancel_charges_per_km_above_base_kms: '',
        effective_from: new Date().toISOString().split('T')[0],
        effective_to: ''
    });

    const fetchServiceRates = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterActive !== null) {
                params.is_active = filterActive;
            }
            
            const response = await serviceRatesService.getServiceRates(params);
            setServiceRates(response.rates || []);
            setVendor(response.vendor);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch service rates');
        } finally {
            setLoading(false);
        }
    }, [filterActive]);

    useEffect(() => {
        fetchServiceRates();
    }, [fetchServiceRates]);

    useEffect(() => {
        loadServiceTypes();
    }, []);

    const loadServiceTypes = async () => {
        try {
            const types = await serviceRatesService.getAllServiceTypes();
            setServiceTypes(types);
        } catch (error) {
            console.error('Error loading service types:', error);
        }
    };

    const handleStatusToggle = useCallback(async (serviceRateId, currentStatus) => {
        try {
            await serviceRatesService.updateServiceRateStatus(serviceRateId, !currentStatus);
            // Refresh service rates after status update
            fetchServiceRates();
        } catch (err) {
            setError(err.message || 'Failed to update service rate status');
        }
    }, [fetchServiceRates]);

    const toggleRowExpansion = useCallback((serviceRateId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(serviceRateId)) {
            newExpandedRows.delete(serviceRateId);
            // If we're collapsing a row that was being edited, cancel edit mode
            if (editingServiceRateId === serviceRateId) {
                setEditingServiceRateId(null);
            }
        } else {
            newExpandedRows.add(serviceRateId);
        }
        setExpandedRows(newExpandedRows);
    }, [expandedRows, editingServiceRateId, setExpandedRows, setEditingServiceRateId]);

    const handleAddServiceRate = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            
            // Validate required fields
            if (!newServiceRate.service_type_code || !newServiceRate.base_service_rate || 
                !newServiceRate.base_max_kms || !newServiceRate.additional_rate_per_km) {
                setError('Please fill in all required fields (service type, base rate, base max KMs, and additional rate per KM)');
                return;
            }

            // Get current user mobile number
            const userMobile = user?.mobile_number;
            
            if (!userMobile) {
                setError('User mobile number not found. Please log in again.');
                return;
            }

            // Prepare registration data
            const registrationData = {
                service_type_code: newServiceRate.service_type_code,
                service_subtype_code: newServiceRate.service_subtype_code || '',
                time_slot: newServiceRate.time_slot,
                time_slot_start: newServiceRate.time_slot_start,
                time_slot_end: newServiceRate.time_slot_end,
                base_service_rate: parseFloat(newServiceRate.base_service_rate),
                base_max_kms: parseFloat(newServiceRate.base_max_kms),
                additional_rate_per_km: parseFloat(newServiceRate.additional_rate_per_km),
                fixed_rate: newServiceRate.fixed_rate ? parseFloat(newServiceRate.fixed_rate) : null,
                cancel_charges_per_km_upto_base_kms: parseFloat(newServiceRate.cancel_charges_per_km_upto_base_kms),
                cancel_charges_per_km_above_base_kms: parseFloat(newServiceRate.cancel_charges_per_km_above_base_kms),
                effective_from: newServiceRate.effective_from,
                effective_to: newServiceRate.effective_to || null
            };

            // Get vendor ID from the vendor state
            if (!vendor || !vendor.id) {
                setError('Vendor information not found. Please refresh the page and try again.');
                return;
            }

            // Create vendor registration for new service rate
            const result = await vendorRegistrationService.createVendorRegistration({
                registration_type: 'new_record_existing_user',
                entity_type: 'vendor_service_rate',
                user_mobile_number: userMobile,
                registration_data: registrationData,
                vendor_id: vendor.id,
                entity_id: null
            });

            if (result.success) {
                // Now submit the registration for approval
                const registrationId = result.registration_id;
                if (registrationId) {
                    try {
                        const submitResult = await vendorRegistrationService.submitRegistration(registrationId);
                        
                        if (submitResult.success) {
                            // Show success message
                            alert('Service rate registration submitted successfully! It will be reviewed and approved by management.');
                        } else {
                            // Show warning that creation succeeded but submission failed
                            alert(`Service rate registration created but submission failed: ${submitResult.error || 'Unknown error'}. Please contact support.`);
                        }
                    } catch (submitError) {
                        // Show warning that creation succeeded but submission failed
                        alert('Service rate registration created but submission failed. Please contact support.');
                    }
                } else {
                    // Show warning that creation succeeded but no ID returned
                    alert('Service rate registration created but could not be submitted. Please contact support.');
                }

                // Reset form and refresh service rates
                setNewServiceRate({
                    service_type_code: '',
                    service_subtype_code: '',
                    time_slot: 'DAY',
                    time_slot_start: '06:00:00',
                    time_slot_end: '18:00:00',
                    base_service_rate: '',
                    base_max_kms: '',
                    additional_rate_per_km: '',
                    fixed_rate: '',
                    cancel_charges_per_km_upto_base_kms: '',
                    cancel_charges_per_km_above_base_kms: '',
                    effective_from: new Date().toISOString().split('T')[0],
                    effective_to: ''
                });
                setShowAddServiceRateRow(false);
                
                // Refresh service rates to show any existing approved rates
                fetchServiceRates();
            } else {
                setError(result.error || 'Failed to submit service rate registration');
            }
        } catch (err) {
            setError(err.message || 'Failed to submit service rate registration');
        } finally {
            setLoading(false);
        }
    }, [newServiceRate, user, vendor, fetchServiceRates]);

    const handleServiceRateChange = useCallback((field, value) => {
        setNewServiceRate(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleEditClick = useCallback((serviceRate) => {
        setEditingServiceRateId(serviceRate.id);
        
        // Expand the row if not already expanded
        if (!expandedRows.has(serviceRate.id)) {
            toggleRowExpansion(serviceRate.id);
        }
    }, [expandedRows, toggleRowExpansion]);

    const handleCancelEdit = useCallback(() => {
        setEditingServiceRateId(null);
        // Collapse the currently expanded row
        if (editingServiceRateId) {
            const newExpandedRows = new Set(expandedRows);
            newExpandedRows.delete(editingServiceRateId);
            setExpandedRows(newExpandedRows);
        }
    }, [editingServiceRateId, expandedRows, setExpandedRows]);

    const handleSaveEdit = useCallback(async (serviceRate, changedFields) => {
        try {
            setLoading(true);
            setError('');
            
            // Get current user mobile number
            const userMobile = user?.mobile_number;
            if (!userMobile) {
                setError('User mobile number not found. Please log in again.');
                return;
            }

            // Validate that we have at least some data to submit
            if (Object.keys(changedFields).length === 0) {
                setError('No valid changes to submit');
                return;
            }

            // Create vendor registration for service rate update
            const result = await vendorRegistrationService.createVendorRegistration({
                registration_type: 'existing_record_update',
                entity_type: 'vendor_service_rate',
                user_mobile_number: userMobile,
                registration_data: changedFields,
                vendor_id: vendor.id,
                entity_id: serviceRate.id // Use the existing service rate ID
            });

            if (result.success) {
                // Now submit the registration for approval
                const registrationId = result.registration_id;
                if (registrationId) {
                    try {
                        const submitResult = await vendorRegistrationService.submitRegistration(registrationId);
                        
                        if (submitResult.success) {
                            // Show success message
                            alert('Service rate update submitted successfully! It will be reviewed and approved by management.');
                            
                            // Exit edit mode and collapse row
                            setEditingServiceRateId(null);
                            const newExpandedRows = new Set(expandedRows);
                            newExpandedRows.delete(serviceRate.id);
                            setExpandedRows(newExpandedRows);
                            
                            // Refresh service rates
                            fetchServiceRates();
                        } else {
                            // Show warning that creation succeeded but submission failed
                            alert(`Service rate update created but submission failed: ${submitResult.error || 'Unknown error'}. Please contact support.`);
                        }
                    } catch (submitError) {
                        alert('Service rate update created but submission failed. Please contact support.');
                    }
                } else {
                    alert('Service rate update created but could not be submitted. Please contact support.');
                }
            } else {
                setError(result.error || 'Failed to submit service rate update');
            }
        } catch (err) {
            setError(err.message || 'Failed to submit service rate update');
        } finally {
            setLoading(false);
        }
    }, [user, vendor, fetchServiceRates, expandedRows, setExpandedRows]);

    const handleDeactivateClick = useCallback((serviceRate) => {
        setServiceRateToDeactivate(serviceRate);
        setShowDeactivateConfirm(true);
    }, []);

    const confirmDeactivate = useCallback(async () => {
        if (!serviceRateToDeactivate) return;
        
        try {
            await handleStatusToggle(serviceRateToDeactivate.id, serviceRateToDeactivate.is_active);
            setShowDeactivateConfirm(false);
            setServiceRateToDeactivate(null);
        } catch (err) {
            // Error handling for deactivation
        }
    }, [serviceRateToDeactivate, handleStatusToggle]);

    const getServiceTypeName = useCallback((serviceTypeCode, serviceTypes) => {
        const serviceType = serviceTypes.find(st => st.service_type_code === serviceTypeCode);
        return serviceType ? serviceType.service_type_name : 'Unknown Service';
    }, []);

    const getServiceSubtypeName = useCallback((serviceTypeCode, serviceSubtypeCode, serviceSubtypes) => {
        const subtypes = serviceSubtypes[serviceTypeCode] || [];
        const subtype = subtypes.find(sst => sst.subtype_code === serviceSubtypeCode);
        return subtype ? subtype.subtype_name : '';
    }, []);

    const getTimeSlotBadge = useCallback((timeSlot) => {
        const badges = {
            'DAY': { color: 'bg-blue-100 text-blue-800', text: 'Day' },
            'NIGHT': { color: 'bg-purple-100 text-purple-800', text: 'Night' }
        };
        
        const badge = badges[timeSlot] || { color: 'bg-gray-100 text-gray-800', text: timeSlot };
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.text}
            </span>
        );
    }, []);

    const ServiceRateRow = useCallback(({ serviceRate }) => {
        const isExpanded = expandedRows.has(serviceRate.id);
        
        return (
            <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">
                {/* Main Row - Clickable */}
                <div 
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => toggleRowExpansion(serviceRate.id)}
                >
                    <div className="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
                        <div className="flex items-start space-x-3 min-w-0 flex-1">
                            <div className="text-gray-500 mt-1 shrink-0">
                                {isExpanded ? (
                                    <ChevronDown className="w-5 h-5" />
                                ) : (
                                    <ChevronRight className="w-5 h-5" />
                                )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base md:text-lg font-semibold text-gray-900 break-words">
                                    {getServiceTypeName(serviceRate.service_type_code, serviceTypes)}
                                    {serviceRate.service_subtype_code && (
                                        <span className="ml-2 text-gray-600 font-normal">
                                            - {getServiceSubtypeName(serviceRate.service_type_code, serviceRate.service_subtype_code, serviceSubtypes)}
                                        </span>
                                    )}
                                    {editingServiceRateId === serviceRate.id && (
                                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            <Edit className="w-3 h-3 mr-1" />
                                            Editing
                                        </span>
                                    )}
                                </h3>
                                <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-1">
                                    {getTimeSlotBadge(serviceRate.time_slot)}
                                    <span>• Base Rate: {localeConfig.currency.symbol}{serviceRate.base_service_rate}</span>
                                    <span>• Max KMs: {serviceRate.base_max_kms}km</span>
                                    <span className="md:hidden">• {localeConfig.currency.symbol}{serviceRate.additional_rate_per_km}/km</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center flex-wrap gap-2 pl-8 md:pl-0 md:space-x-3 shrink-0">
                            <div className="hidden md:block text-sm text-gray-500">
                                {localeConfig.currency.symbol}{serviceRate.additional_rate_per_km}/km
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(serviceRate);
                                }}
                                className="px-3 py-1 text-xs font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                            >
                                <Edit className="w-3 h-3 inline mr-1" />
                                Edit
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeactivateClick(serviceRate);
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                    serviceRate.is_active
                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                            >
                                {serviceRate.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                    <>
                        {editingServiceRateId === serviceRate.id ? (
                            /* Edit Form */
                            <EditServiceRateRow
                                key={`edit-${serviceRate.id}`}
                                serviceRate={serviceRate}
                                onCancelEdit={handleCancelEdit}
                                onSaveEdit={(changedFields) => handleSaveEdit(serviceRate, changedFields)}
                                loading={loading}
                            />
                        ) : (
                            /* Normal View */
                            <div className="border-t border-gray-200 bg-gray-50 p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Service Details */}
                                    <div className="space-y-3">
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-2">Service Details</h4>
                                            <div className="bg-white p-3 rounded-md">
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="font-medium">Service Type:</span>
                                                        <p className="text-gray-900">{getServiceTypeName(serviceRate.service_type_code, serviceTypes)}</p>
                                                    </div>
                                                    {serviceRate.service_subtype_code && (
                                                        <div>
                                                            <span className="font-medium">Subtype:</span>
                                                            <p className="text-gray-900">{getServiceSubtypeName(serviceRate.service_type_code, serviceRate.service_subtype_code, serviceSubtypes)}</p>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="font-medium">Time Slot:</span>
                                                        <p className="text-gray-900">{getTimeSlotBadge(serviceRate.time_slot)}</p>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Time Range:</span>
                                                        <p className="text-gray-900">{serviceRate.time_slot_start} - {serviceRate.time_slot_end}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Rate Details */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-2">Rate Details</h4>
                                        <div className="bg-white p-3 rounded-md">
                                            <div className="grid grid-cols-1 gap-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="font-medium">Base Rate:</span>
                                                    <span className="text-gray-600">{localeConfig.currency.symbol}{serviceRate.base_service_rate}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="font-medium">Base Max KMs:</span>
                                                    <span className="text-gray-600">{serviceRate.base_max_kms} km</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="font-medium">Additional Rate per KM:</span>
                                                    <span className="text-gray-600">{localeConfig.currency.symbol}{serviceRate.additional_rate_per_km}</span>
                                                </div>
                                                {serviceRate.fixed_rate && (
                                                    <div className="flex justify-between">
                                                        <span className="font-medium">Fixed Rate:</span>
                                                        <span className="text-gray-600">{localeConfig.currency.symbol}{serviceRate.fixed_rate}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between">
                                                    <span className="font-medium">Cancel Charges (&le; Base KMs):</span>
                                                    <span className="text-gray-600">{localeConfig.currency.symbol}{serviceRate.cancel_charges_per_km_upto_base_kms}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="font-medium">Cancel Charges (&gt; Base KMs):</span>
                                                    <span className="text-gray-600">{localeConfig.currency.symbol}{serviceRate.cancel_charges_per_km_above_base_kms}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Effective Dates */}
                                <div className="mt-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Effective Dates</h4>
                                    <div className="bg-white p-3 rounded-md">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="font-medium">Effective From:</span>
                                                <p className="text-gray-900">{formatDate(serviceRate.effective_from)}</p>
                                            </div>
                                            {serviceRate.effective_to && (
                                                <div>
                                                    <span className="font-medium">Effective To:</span>
                                                    <p className="text-gray-900">{formatDate(serviceRate.effective_to)}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 text-xs text-gray-500">
                                    Created: {formatDate(serviceRate.created_at)}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }, [
        expandedRows,
        toggleRowExpansion,
        handleDeactivateClick,
        getServiceTypeName,
        getServiceSubtypeName,
        getTimeSlotBadge,
        serviceTypes,
        serviceSubtypes,
        editingServiceRateId,
        handleCancelEdit,
        handleEditClick,
        handleSaveEdit,
        loading,
    ]);

    if (loading) {
        return (
            <div className="page-container">
                <div className="content-container">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="content-container">
                {/* Header */}
                <div className="card mb-4 md:mb-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                                Service Rates Management
                            </h1>
                            <p className="text-gray-600 mt-1 text-sm md:text-base">
                                {vendor ? `Manage service rates for ${vendor.vendor_name}` : 'Manage your vendor service rates and pricing'}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
                            {/* Filter */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700 shrink-0">Filter:</label>
                                <select
                                    value={filterActive === null ? '' : filterActive.toString()}
                                    onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Rates</option>
                                    <option value="true">Active Only</option>
                                    <option value="false">Inactive Only</option>
                                </select>
                            </div>
                            
                            {/* Refresh Button */}
                            <button
                                onClick={fetchServiceRates}
                                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="card mb-6 bg-red-50 border-red-200">
                        <div className="flex items-center space-x-2 text-red-700">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    </div>
                )}

                {/* Service Rates List */}
                {serviceRates.length > 0 ? (
                    <div className="space-y-4">
                        {serviceRates.map((serviceRate) => (
                            <ServiceRateRow key={serviceRate.id} serviceRate={serviceRate} />
                        ))}
                        
                        {/* Add Service Rate Row - Now within the same spacing container */}
                        {showAddServiceRateRow && (
                            <AddServiceRateRow
                                showAddServiceRateRow={showAddServiceRateRow}
                                setShowAddServiceRateRow={setShowAddServiceRateRow}
                                newServiceRate={newServiceRate}
                                loading={loading}
                                handleServiceRateChange={handleServiceRateChange}
                                handleOperatingHoursChange={() => {}} // Not needed for service rates
                                handleAddServiceRate={handleAddServiceRate}
                                formatMobileNumber={() => {}} // Not needed for service rates
                            />
                        )}
                    </div>
                ) : (
                    <div className="card">
                        <div className="text-center py-12">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                No Service Rates Found
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                {filterActive !== null 
                                    ? `No ${filterActive ? 'active' : 'inactive'} service rates found.`
                                    : 'No service rates have been configured for your vendor account yet.'
                                }
                            </p>
                        </div>
                    </div>
                )}

                {/* Add Service Rate Row - For when there are no existing rates */}
                {serviceRates.length === 0 && showAddServiceRateRow && (
                    <div className="mt-4">
                        <AddServiceRateRow
                            showAddServiceRateRow={showAddServiceRateRow}
                            setShowAddServiceRateRow={setShowAddServiceRateRow}
                            newServiceRate={newServiceRate}
                            loading={loading}
                            handleServiceRateChange={handleServiceRateChange}
                            handleOperatingHoursChange={() => {}} // Not needed for service rates
                            handleAddServiceRate={handleAddServiceRate}
                            formatMobileNumber={() => {}} // Not needed for service rates
                        />
                    </div>
                )}

                {/* Add Service Rate Button - Below the rows */}
                {!showAddServiceRateRow && (
                    <div className="text-center py-6">
                        <button
                            onClick={() => setShowAddServiceRateRow(true)}
                            className="flex items-center space-x-2 px-4 sm:px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors mx-auto text-sm sm:text-base"
                        >
                            <Plus className="w-5 h-5" />
                            <span className="hidden sm:inline">Submit Service Rate Registration</span>
                            <span className="sm:hidden">Add Service Rate</span>
                        </button>
                    </div>
                )}

                {/* Deactivate Confirmation Modal */}
                {showDeactivateConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                        <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                </div>
                                
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Confirm Deactivation
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-6">
                                        Are you sure you want to deactivate the service rate for{' '}
                                        <span className="font-medium">
                                            {getServiceTypeName(serviceRateToDeactivate?.service_type_code, serviceTypes)}
                                        </span>?
                                        <br />
                                        <span className="text-red-600">
                                            This action cannot be undone.
                                        </span>
                                    </p>
                                    
                                    <div className="flex justify-center space-x-3">
                                        <button
                                            onClick={() => {
                                                setShowDeactivateConfirm(false);
                                                setServiceRateToDeactivate(null);
                                            }}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmDeactivate}
                                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                                        >
                                            Deactivate
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServiceRates; 